import { create } from "zustand";
import { useNotificationStore } from "./useNotificationStore";
import { getRankProgress } from "@/lib/titles";
import i18n from "@/i18n/i18n";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Achievement stats for individual badges (Награды tab) */
export interface GamificationStats {
  wordsLearned: number;
  aiWordsGenerated: number;
  testsPassed: number;
  cardsViewed: number;
  decksCreated: number;
  cardsWithImages: number;
  earlyBirdSessions: number;
  nightOwlSessions: number;
  totalCrystalsEarned: number;
  /** Time Mastery: total study minutes (lifetime) */
  totalStudyMinutes: number;
  /** Time Mastery: total app/site minutes (lifetime) */
  totalSiteMinutes: number;
}

export const DEFAULT_STATS: GamificationStats = {
  wordsLearned: 0,
  aiWordsGenerated: 0,
  testsPassed: 0,
  cardsViewed: 0,
  decksCreated: 0,
  cardsWithImages: 0,
  earlyBirdSessions: 0,
  nightOwlSessions: 0,
  totalCrystalsEarned: 0,
  totalStudyMinutes: 0,
  totalSiteMinutes: 0,
};

/** Payload for completing a learning session (test, study run, etc.) */
export interface CompleteLearningSessionPayload {
  xpReward: number;
  crystalReward: number;
  statsIncrease?: {
    wordsLearned?: number;
    testsPassed?: number;
    cardsViewed?: number;
  };
}

/** Shop categories that support equipping (one item per category) */
export type EquipCategory = "themes" | "card_skins";

/** Tab IDs in the Achievements/Gamification modal */
export type AchievementsTabId = "badges" | "streaks" | "titles" | "shop";

export interface GamificationState {
  crystals: number;
  xp: number;
  currentStreak: number;
  lastActivityDate: string | null;
  studyHistory: string[]; // YYYY-MM-DD dates when user studied
  ownedItems: string[];
  equippedItems: string[];
  equippedTheme: string | null; // itemId e.g. 'matrixTheme', 'neonTheme', or null when unequipped
  equippedCardSkin: string; // itemId e.g. 'fireBorder', 'holographicBorder', or 'default'
  stats: GamificationStats;
  /** UI: whether the achievements modal is open */
  achievementsModalOpen: boolean;
  /** UI: initial tab to show when opening the modal (e.g. from notification) */
  achievementsInitialTab: AchievementsTabId | null;
  /** Last tier index we showed "Medal Unlocked" for study time (-1 = none) */
  lastNotifiedStudyTier: number;
  /** Last tier index we showed "Medal Unlocked" for site time (-1 = none) */
  lastNotifiedSiteTier: number;
  /** Last rank index we showed "Medal Unlocked" toast for (XP-based rank; -1 = none) */
  lastNotifiedRankIndex: number;
  /** Streak milestones we already showed "Medal Unlocked" for (e.g. [7, 14, 30]) */
  lastNotifiedStreakMilestones: number[];
}

export interface GamificationActions {
  setState: (state: Partial<GamificationState>) => void;
  hydrate: (state: GamificationState) => void;
  addCrystals: (amount: number, options?: { isLearningAction?: boolean }) => AddCrystalsResult;
  spendCrystals: (amount: number) => void;
  addXp: (amount: number) => void;
  setStreak: (streak: number) => void;
  setLastActivityDate: (date: string | null) => void;
  addOwnedItem: (itemId: string) => void;
  removeOwnedItem: (itemId: string) => void;
  setOwnedItems: (ids: string[]) => void;
  equipItem: (itemId: string, category?: EquipCategory) => void;
  unequipItem: (itemId: string) => void;
  clearEquippedTheme: () => void;
  setEquippedItems: (ids: string[]) => void;
  incrementStat: <K extends keyof GamificationStats>(key: K, amount?: number) => void;
  completeLearningSession: (payload: CompleteLearningSessionPayload) => AddCrystalsResult | undefined;
  finishStudySession: (wordsCount: number, isTest: boolean) => void;
  openAchievementsModal: (tab?: AchievementsTabId) => void;
  closeAchievementsModal: () => void;
  /** Sync time totals from activity and check for new medal milestones */
  syncTimeTotals: (totalStudyMinutes: number, totalSiteMinutes: number) => void;
}

export type GamificationStore = GamificationState & GamificationActions;

// ─── localStorage fallback (for anonymous users) ───────────────────────────────

const STORAGE_KEY = "ld_gamification";

/** Shop item ID for Crystal Doubler — doubles crystal earnings when equipped */
const CRYSTAL_DOUBLER_ID = "crystalDoubler";

export type AddCrystalsResult = { totalAdd: number; multiplierApplied: boolean };

function loadFromLocalStorage(): GamificationState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const stats = parsed.stats && typeof parsed.stats === "object"
        ? { ...DEFAULT_STATS, ...parsed.stats }
        : DEFAULT_STATS;
      const studyHistory = Array.isArray(parsed.studyHistory)
        ? parsed.studyHistory.filter((d): d is string => typeof d === "string")
        : [];
      return {
        crystals: Number(parsed.crystals) || 0,
        xp: Number(parsed.xp) || 0,
        currentStreak: Number(parsed.currentStreak) || 0,
        lastActivityDate: parsed.lastActivityDate ?? null,
        studyHistory,
        ownedItems: Array.isArray(parsed.ownedItems) ? parsed.ownedItems : [],
        equippedItems: Array.isArray(parsed.equippedItems) ? parsed.equippedItems : [],
        equippedTheme:
          typeof parsed.equippedTheme === "string" &&
          parsed.equippedTheme !== "default" &&
          parsed.equippedTheme !== "defaultTheme"
            ? parsed.equippedTheme
            : null,
        equippedCardSkin: typeof parsed.equippedCardSkin === "string" ? parsed.equippedCardSkin : "default",
        stats,
        achievementsModalOpen: false,
        achievementsInitialTab: null,
        lastNotifiedStudyTier: typeof parsed.lastNotifiedStudyTier === "number" ? parsed.lastNotifiedStudyTier : -1,
        lastNotifiedSiteTier: typeof parsed.lastNotifiedSiteTier === "number" ? parsed.lastNotifiedSiteTier : -1,
        lastNotifiedRankIndex: typeof parsed.lastNotifiedRankIndex === "number" ? parsed.lastNotifiedRankIndex : -1,
        lastNotifiedStreakMilestones: Array.isArray(parsed.lastNotifiedStreakMilestones)
          ? parsed.lastNotifiedStreakMilestones.filter((n: unknown) => typeof n === "number")
          : [],
      };
    }
  } catch (_) {}
  return {
    crystals: 0,
    xp: 0,
    currentStreak: 0,
    lastActivityDate: null,
    studyHistory: [],
    ownedItems: [],
    equippedItems: [],
    equippedTheme: null,
    equippedCardSkin: "default",
    stats: { ...DEFAULT_STATS },
    achievementsModalOpen: false,
    achievementsInitialTab: null,
    lastNotifiedStudyTier: -1,
    lastNotifiedSiteTier: -1,
    lastNotifiedRankIndex: -1,
    lastNotifiedStreakMilestones: [],
  };
}

function saveToLocalStorage(state: GamificationState) {
  try {
    const { achievementsModalOpen, achievementsInitialTab, ...persistable } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
  } catch (_) {}
}

/** Time Mastery thresholds in minutes: 1h, 6h, 24h, 120h (5d), 720h (30d) */
const TIME_STUDY_THRESHOLDS = [60, 360, 1440, 7200, 43200];
/** Time Mastery app thresholds: 2h, 12h, 72h (3d), 360h (15d), 1440h (60d) */
const TIME_SITE_THRESHOLDS = [120, 720, 4320, 21600, 86400];
function getTimeTierName(tier: number): string {
  return i18n.t(`gamification.timeTier.${tier}`);
}
function getTimeSiteTierName(tier: number): string {
  return i18n.t(`gamification.timeSiteTier.${tier}`);
}

function getTierIndex(thresholds: number[], value: number): number {
  let tier = -1;
  for (let i = 0; i < thresholds.length; i++) {
    if (value >= thresholds[i]) tier = i;
  }
  return tier;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

function todayYYYYMMDD(): string {
  return new Date().toISOString().slice(0, 10);
}

function yesterdayYYYYMMDD(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useGamificationStore = create<GamificationStore>((set, get) => ({
  ...loadFromLocalStorage(),

  setState: (updates) => {
    set((prev) => {
      const next = { ...prev, ...updates };
      saveToLocalStorage(next);
      return next;
    });
  },

  hydrate: (state) => {
    set((prev) => {
      const next = { ...prev, ...state };
      saveToLocalStorage(next);
      return next;
    });
  },

  addCrystals: (amount, options) => {
    const isLearningAction = options?.isLearningAction ?? false;
    const today = todayYYYYMMDD();
    const yesterday = yesterdayYYYYMMDD();
    const { lastActivityDate, currentStreak, equippedItems } = get();

    let bonusCrystals = 0;
    let newStreak = currentStreak;

    if (isLearningAction && lastActivityDate !== today) {
      if (!lastActivityDate || lastActivityDate === yesterday) {
        newStreak = lastActivityDate === yesterday ? currentStreak + 1 : 1;
        bonusCrystals += 5;
      } else {
        newStreak = 1;
        bonusCrystals += 5;
      }

      if (newStreak > 0 && newStreak % 7 === 0) {
        const weekNumber = newStreak / 7;
        bonusCrystals += 50 * weekNumber;
      }
    }

    const baseEarned = amount + bonusCrystals;
    const multiplierApplied = equippedItems.includes(CRYSTAL_DOUBLER_ID);
    const totalAdd = multiplierApplied ? baseEarned * 2 : baseEarned;

    set((prev) => {
      const newStudyHistory =
        isLearningAction && today && !prev.studyHistory.includes(today)
          ? [...prev.studyHistory, today].sort().slice(-365) // Keep last 365 days
          : prev.studyHistory;
      const next = {
        ...prev,
        crystals: prev.crystals + totalAdd,
        currentStreak: isLearningAction && lastActivityDate !== today ? newStreak : prev.currentStreak,
        lastActivityDate: isLearningAction && lastActivityDate !== today ? today : prev.lastActivityDate,
        studyHistory: newStudyHistory,
        stats: {
          ...prev.stats,
          totalCrystalsEarned: prev.stats.totalCrystalsEarned + totalAdd,
        },
      };
      saveToLocalStorage(next);
      return next;
    });

    return { totalAdd, multiplierApplied };
  },

  spendCrystals: (amount) => {
    set((prev) => {
      const next = {
        ...prev,
        crystals: Math.max(0, prev.crystals - amount),
      };
      saveToLocalStorage(next);
      return next;
    });
  },

  addXp: (amount) => {
    set((prev) => {
      const next = { ...prev, xp: prev.xp + amount };
      saveToLocalStorage(next);
      return next;
    });
  },

  setStreak: (streak) => {
    set((prev) => {
      const next = { ...prev, currentStreak: Math.max(0, streak) };
      saveToLocalStorage(next);
      return next;
    });
  },

  setLastActivityDate: (date) => {
    set((prev) => {
      const next = { ...prev, lastActivityDate: date };
      saveToLocalStorage(next);
      return next;
    });
  },

  addOwnedItem: (itemId) => {
    set((prev) => {
      if (prev.ownedItems.includes(itemId)) return prev;
      const next = { ...prev, ownedItems: [...prev.ownedItems, itemId] };
      saveToLocalStorage(next);
      return next;
    });
  },

  removeOwnedItem: (itemId) => {
    set((prev) => {
      const next = {
        ...prev,
        ownedItems: prev.ownedItems.filter((id) => id !== itemId),
        equippedItems: prev.equippedItems.filter((id) => id !== itemId),
      };
      saveToLocalStorage(next);
      return next;
    });
  },

  setOwnedItems: (ids) => {
    set((prev) => {
      const next = { ...prev, ownedItems: ids };
      saveToLocalStorage(next);
      return next;
    });
  },

  equipItem: (itemId, category) => {
    set((prev) => {
      const updates: Partial<GamificationState> = {};
      if (category === "themes") {
        if (!prev.ownedItems.includes(itemId)) return prev;
        // Toggle: if already equipped, unequip (set to null)
        updates.equippedTheme = prev.equippedTheme === itemId ? null : itemId;
      } else if (category === "card_skins") {
        if (!prev.ownedItems.includes(itemId)) return prev;
        updates.equippedCardSkin = itemId;
      } else {
        if (!prev.ownedItems.includes(itemId)) return prev;
        // Fallback: add to equippedItems for boosters or unknown
        const alreadyEquipped = prev.equippedItems.includes(itemId);
        if (!alreadyEquipped) {
          updates.equippedItems = [...prev.equippedItems, itemId];
        }
      }
      if (Object.keys(updates).length === 0) return prev;
      const next = { ...prev, ...updates };
      saveToLocalStorage(next);
      return next;
    });
  },

  unequipItem: (itemId) => {
    set((prev) => {
      const updates: Partial<GamificationState> = {};
      if (prev.equippedTheme === itemId) updates.equippedTheme = null;
      if (prev.equippedCardSkin === itemId) updates.equippedCardSkin = "default";
      updates.equippedItems = prev.equippedItems.filter((id) => id !== itemId);
      const next = { ...prev, ...updates };
      saveToLocalStorage(next);
      return next;
    });
  },

  clearEquippedTheme: () => {
    set((prev) => {
      if (prev.equippedTheme == null) return prev;
      const next = { ...prev, equippedTheme: null };
      saveToLocalStorage(next);
      return next;
    });
  },

  setEquippedItems: (ids) => {
    set((prev) => {
      const next = { ...prev, equippedItems: ids };
      saveToLocalStorage(next);
      return next;
    });
  },

  incrementStat: (key, amount = 1) => {
    set((prev) => {
      const next = {
        ...prev,
        stats: {
          ...prev.stats,
          [key]: Math.max(0, (prev.stats[key] ?? 0) + amount),
        },
      };
      saveToLocalStorage(next);
      return next;
    });
  },

  completeLearningSession: (payload) => {
    const { xpReward, crystalReward, statsIncrease } = payload;
    const store = get();

    // Add XP
    set((prev) => {
      const next = { ...prev, xp: prev.xp + xpReward };
      saveToLocalStorage(next);
      return next;
    });

    // Add crystals (with streak/daily logic, Crystal Doubler, and studyHistory update)
    const addResult = store.addCrystals(crystalReward, { isLearningAction: true });

    // Increment stats
    if (statsIncrease) {
      if (statsIncrease.wordsLearned) store.incrementStat("wordsLearned", statsIncrease.wordsLearned);
      if (statsIncrease.testsPassed) store.incrementStat("testsPassed", statsIncrease.testsPassed);
      if (statsIncrease.cardsViewed) store.incrementStat("cardsViewed", statsIncrease.cardsViewed);
    }

    return addResult;
  },

  finishStudySession: (wordsCount, isTest) => {
    const store = get();
    const today = todayYYYYMMDD();
    const yesterday = yesterdayYYYYMMDD();
    const { lastActivityDate, currentStreak, equippedItems, xp: prevXp } = store;

    // Base rewards
    const baseXp = wordsCount * 10;
    const baseCrystals = wordsCount * 5;

    // Crystal multiplier (Crystal Doubler)
    const multiplierApplied = equippedItems.includes(CRYSTAL_DOUBLER_ID);
    const finalCrystals = multiplierApplied ? baseCrystals * 2 : baseCrystals;

    // Streak update: if not studied today, update streak
    let newStreak = currentStreak;
    let newStudyHistory = store.studyHistory;
    if (lastActivityDate !== today) {
      if (!lastActivityDate || lastActivityDate === yesterday) {
        newStreak = lastActivityDate === yesterday ? currentStreak + 1 : 1;
      } else {
        newStreak = 1;
      }
      if (!store.studyHistory.includes(today)) {
        newStudyHistory = [...store.studyHistory, today].sort().slice(-365);
      }
    }

    set((prev) => {
      const next = {
        ...prev,
        xp: prev.xp + baseXp,
        crystals: prev.crystals + finalCrystals,
        lastActivityDate: today,
        currentStreak: lastActivityDate !== today ? newStreak : prev.currentStreak,
        studyHistory: lastActivityDate !== today ? newStudyHistory : prev.studyHistory,
        stats: {
          ...prev.stats,
          wordsLearned: prev.stats.wordsLearned + wordsCount,
          testsPassed: isTest ? prev.stats.testsPassed + 1 : prev.stats.testsPassed,
          totalCrystalsEarned: prev.stats.totalCrystalsEarned + finalCrystals,
        },
      };
      saveToLocalStorage(next);
      return next;
    });

    // Trigger reward notification only for substantial sessions (avoid shop spam)
    // For tests, the TestStudyView dispatches its own test-specific notification when >= 10 cards
    if (wordsCount >= 10 && !isTest) {
      useNotificationStore.getState().addNotification({
        kind: "WORKOUT_COMPLETED",
        payload: { crystals: finalCrystals, xp: baseXp },
        displayType: "ROUTE",
        target: "/library",
        iconType: "study",
      });
    }

    // Check for rank up (XP-based)
    const newXp = prevXp + baseXp;
    const oldRank = getRankProgress(prevXp).current.id;
    const newRank = getRankProgress(newXp).current.id;
    if (newRank !== oldRank) {
      useNotificationStore.getState().addNotification({
        kind: "RANK_UP",
        payload: { rankId: newRank },
        displayType: "MODAL",
        target: "achievements",
        iconType: "achievement",
      });
    }
  },

  openAchievementsModal: (tab) => {
    const validTabs: AchievementsTabId[] = ["badges", "streaks", "titles", "shop"];
    const initialTab =
      tab && validTabs.includes(tab as AchievementsTabId) ? (tab as AchievementsTabId) : null;
    set({
      achievementsModalOpen: true,
      achievementsInitialTab: initialTab,
    });
  },

  closeAchievementsModal: () => {
    set({
      achievementsModalOpen: false,
      achievementsInitialTab: null,
    });
  },

  syncTimeTotals: (totalStudyMinutes, totalSiteMinutes) => {
    set((prev) => {
      const studyTier = getTierIndex(TIME_STUDY_THRESHOLDS, totalStudyMinutes);
      const siteTier = getTierIndex(TIME_SITE_THRESHOLDS, totalSiteMinutes);
      let lastStudy = prev.lastNotifiedStudyTier;
      let lastSite = prev.lastNotifiedSiteTier;

      if (studyTier > prev.lastNotifiedStudyTier && studyTier >= 0) {
        lastStudy = studyTier;
        const label = getTimeTierName(studyTier);
        useNotificationStore.getState().addNotification({
          kind: "MEDAL_TIME_MASTER",
          payload: { label },
          displayType: "MODAL",
          target: "achievements",
          iconType: "achievement",
        });
      }
      if (siteTier > prev.lastNotifiedSiteTier && siteTier >= 0) {
        lastSite = siteTier;
        const label = getTimeSiteTierName(siteTier);
        useNotificationStore.getState().addNotification({
          kind: "MEDAL_APP_CHAMPION",
          payload: { label },
          displayType: "MODAL",
          target: "achievements",
          iconType: "achievement",
        });
      }

      const next = {
        ...prev,
        stats: { ...prev.stats, totalStudyMinutes, totalSiteMinutes },
        lastNotifiedStudyTier: lastStudy,
        lastNotifiedSiteTier: lastSite,
      };
      saveToLocalStorage(next);
      return next;
    });
  },
}));
